import { useQuery } from '@tanstack/react-query';
import type { Paginated } from '@barbeiropro/contracts';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { useTableParams } from '@/components/shared/use-table-params';
import { api } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import { useTimezone } from '@/lib/org';
import { Section } from './section';

interface AuditRow {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  changes: unknown;
  actorName: string | null;
  createdAt: string;
}

const ACTIONS: Record<string, string> = {
  'organization.updated': 'Dados da barbearia alterados',
  'branch.created': 'Unidade criada',
  'branch.updated': 'Unidade alterada',
  'chair.created': 'Cadeira criada',
  'chair.updated': 'Cadeira alterada',
  'chair.deleted': 'Cadeira excluída',
  'business_hours.replaced': 'Horário de funcionamento alterado',
  'holiday.created': 'Feriado criado',
  'holiday.deleted': 'Feriado removido',
  'booking_policy.updated': 'Regras de agendamento alteradas',
  'member.updated': 'Membro alterado',
  'member.invited': 'Convite enviado',
  'member.invite_revoked': 'Convite revogado',
  'role.created': 'Papel criado',
  'role.updated': 'Papel alterado',
  'role.deleted': 'Papel excluído',
  'service.created': 'Serviço criado',
  'service.price_changed': 'Preço de serviço alterado',
  'service.deleted': 'Serviço excluído',
  'service.restored': 'Serviço restaurado',
  'barber.created': 'Profissional criado',
  'barber.deleted': 'Profissional excluído',
  'barber.services_updated': 'Serviços do profissional alterados',
};
const ENTITIES: Record<string, string> = {
  organization: 'Barbearia',
  branch: 'Unidade',
  chair: 'Cadeira',
  holiday: 'Feriado',
  membership: 'Membro',
  invitation: 'Convite',
  role: 'Papel',
  service: 'Serviço',
  barber: 'Profissional',
};

const label = (a: string) => ACTIONS[a] ?? a;

function details(c: unknown) {
  if (c == null || (typeof c === 'object' && Object.keys(c as object).length === 0)) return '';
  const s = JSON.stringify(c);
  return s.length > 120 ? `${s.slice(0, 120)}…` : s;
}

export function AuditPage() {
  const tz = useTimezone();
  const params = useTableParams({ pageSize: 20 });
  const q = useQuery({
    queryKey: ['org', 'audit', params.query],
    queryFn: () => api.get<Paginated<AuditRow>>('/org/audit', params.query),
    placeholderData: (prev) => prev,
  });

  const columns: DataColumn<AuditRow>[] = [
    { id: 'when', header: 'Quando', cell: ({ row }) => <span className="tabular whitespace-nowrap">{fmtDateTime(row.original.createdAt, tz)}</span> },
    { id: 'who', header: 'Quem', cell: ({ row }) => row.original.actorName ?? <span className="text-muted-foreground">Sistema</span> },
    { id: 'what', header: 'O que', cell: ({ row }) => <span className="font-medium">{label(row.original.action)}</span> },
    { id: 'entity', header: 'Registro', cell: ({ row }) => ENTITIES[row.original.entityType] ?? row.original.entityType },
    {
      id: 'details',
      header: 'Detalhes',
      cell: ({ row }) => {
        const d = details(row.original.changes);
        return d ? (
          <code title={JSON.stringify(row.original.changes)} className="block max-w-xs truncate text-xs text-muted-foreground">
            {d}
          </code>
        ) : null;
      },
    },
  ];

  return (
    <Section title="Auditoria" description="Registro das ações sensíveis feitas no sistema: quem fez, o quê e quando.">
      <DataTable
        columns={columns}
        data={q.data}
        params={params}
        isLoading={q.isLoading}
        error={q.error}
        onRetry={() => q.refetch()}
        searchPlaceholder="Buscar por pessoa ou ação"
        empty={{ title: 'Nenhum registro encontrado', description: 'As ações sensíveis passam a aparecer aqui assim que acontecerem.' }}
      />
    </Section>
  );
}
